import { useCallback, useEffect, useMemo, useState } from "react";
import type { DocumentType, LearnedAlias } from "../api";
import {
  createLearnedAlias,
  deleteLearnedAlias,
  deleteType,
  listLearnedAliases,
  setLearnedAliasDisabled,
  updateLearnedAliasLabel,
  updateType,
} from "../api";

type Props = {
  open: boolean;
  onClose: () => void;
  types: DocumentType[];
  typesLoading: boolean;
  onTypesChanged: () => void;
};

type Tab = "types" | "aliases";

/**
 * Config-beheer modal. Twee tabs:
 * - Types: view + delete (zie TypesDropdown — hier netter gepresenteerd).
 * - Geleerde labels: per doctype/veld/taal gegroepeerd, inline edit label,
 *   soft-disable toggle, hard-delete, en een "nieuw toevoegen"-form.
 *
 * Alle writes gaan direct naar de API (geen local-state buffering) — de modal
 * refresht de lijst na elke write zodat je altijd de echte DB-staat ziet.
 */
export function ConfigModal({ open, onClose, types, typesLoading, onTypesChanged }: Props) {
  const [tab, setTab] = useState<Tab>("types");

  // ESC sluit; body-scroll-lock terwijl open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        // Alleen sluiten als click daadwerkelijk op de backdrop was (niet in de modal zelf).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mt-16 w-full max-w-4xl rounded-lg bg-white shadow-xl flex flex-col max-h-[80vh]">
        <header className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="text-lg font-semibold">Config</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            title="Sluiten (Esc)"
          >
            ×
          </button>
        </header>

        <nav className="flex border-b px-5">
          <TabBtn active={tab === "types"} onClick={() => setTab("types")}>
            Types ({typesLoading ? "…" : types.length})
          </TabBtn>
          <TabBtn active={tab === "aliases"} onClick={() => setTab("aliases")}>
            Geleerde labels
          </TabBtn>
        </nav>

        <div className="flex-1 overflow-y-auto">
          {tab === "types" && (
            <TypesTab types={types} loading={typesLoading} onChanged={onTypesChanged} />
          )}
          {tab === "aliases" && <AliasesTab />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

// ----- Types-tab -----------------------------------------------------------

function TypesTab({
  types,
  loading,
  onChanged,
}: {
  types: DocumentType[];
  loading: boolean;
  onChanged: () => void;
}) {
  if (loading) return <div className="p-5 text-sm text-gray-500">Laden…</div>;
  if (types.length === 0) {
    return (
      <div className="p-5 text-sm text-gray-500">
        Nog geen getrainde types. Upload een document om te beginnen.
      </div>
    );
  }

  return (
    <ul className="divide-y">
      {types.map((t) => (
        <TypeRow key={t.id} type={t} onChanged={onChanged} />
      ))}
    </ul>
  );
}

function TypeRow({ type: t, onChanged }: { type: DocumentType; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(t.naam);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const next = draft.trim();
    if (!next || next === t.naam) {
      setEditing(false);
      setDraft(t.naam);
      return;
    }
    setBusy(true);
    try {
      await updateType(t.id, { naam: next });
      await onChanged();
      setEditing(false);
    } catch (e) {
      alert(`Kon naam niet opslaan: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Type '${t.naam}' verwijderen?`)) return;
    await deleteType(t.id);
    onChanged();
  };

  return (
    <li className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50">
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") {
                  setEditing(false);
                  setDraft(t.naam);
                }
              }}
              disabled={busy}
              className="flex-1 rounded border px-2 py-1 text-sm font-medium"
            />
            <button
              onClick={save}
              disabled={busy}
              className="text-xs rounded bg-blue-600 text-white px-3 py-1 hover:bg-blue-700"
            >
              Opslaan
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setDraft(t.naam);
              }}
              disabled={busy}
              className="text-xs text-gray-500 hover:text-gray-700 px-2"
            >
              Annuleren
            </button>
          </div>
        ) : (
          <div className="font-medium">{t.naam}</div>
        )}
        {t.afzender && <div className="text-xs text-gray-500 mt-0.5">{t.afzender}</div>}
        <div className="text-xs text-gray-400 mt-0.5">
          {t.categorie || "?"} · {t.taal || "?"} · {t.fingerprint.anchors.length} anchors ·{" "}
          <span className="font-mono">{t.gebruik_count}×</span>
        </div>
      </div>
      {!editing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 px-3 py-1"
          >
            Hernoemen
          </button>
          <button
            onClick={handleDelete}
            className="text-xs rounded border border-red-200 bg-white text-red-600 hover:bg-red-50 px-3 py-1"
          >
            Verwijder
          </button>
        </div>
      )}
    </li>
  );
}

// ----- Aliases-tab ---------------------------------------------------------

function AliasesTab() {
  const [items, setItems] = useState<LearnedAlias[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [doctypeFilter, setDoctypeFilter] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listLearnedAliases(doctypeFilter || undefined, includeDisabled);
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [doctypeFilter, includeDisabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Alle unieke doctypes uit huidige items voor filter-dropdown
  const availableDoctypes = useMemo(() => {
    const s = new Set<string>();
    items.forEach((a) => s.add(a.doctype));
    return Array.from(s).sort();
  }, [items]);

  // Groepeer items voor rendering: doctype → veld_canoniek → taal → list
  const grouped = useMemo(() => {
    const out: Record<string, Record<string, Record<string, LearnedAlias[]>>> = {};
    for (const a of items) {
      ((out[a.doctype] ??= {})[a.veld_canoniek] ??= {})[a.taal] ??= [];
      out[a.doctype][a.veld_canoniek][a.taal].push(a);
    }
    return out;
  }, [items]);

  return (
    <div className="px-5 py-4">
      {/* Toolbar: filter + nieuwe toevoegen */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={doctypeFilter}
          onChange={(e) => setDoctypeFilter(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">Alle doctypes</option>
          {availableDoctypes.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          />
          Toon disabled
        </label>
        <div className="flex-1" />
        <AddAliasForm onCreated={refresh} />
      </div>

      {error && (
        <div className="mb-3 rounded bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>
      )}

      {loading && <div className="text-sm text-gray-500">Laden…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-gray-500">
          Geen geleerde label-aliassen (nog). Komen vanzelf binnen bij OCR-typo's of nieuwe talen,
          of voeg er handmatig een toe via "Nieuw…" hierboven.
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="space-y-5">
          {Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([doctype, perVeld]) => (
              <section key={doctype}>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">{doctype}</h3>
                <div className="rounded border divide-y">
                  {Object.entries(perVeld)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([veld, perLang]) => (
                      <div key={veld} className="p-3">
                        <div className="text-xs font-medium text-gray-600 mb-1">{veld}</div>
                        <div className="space-y-1">
                          {Object.entries(perLang)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([lang, aliases]) => (
                              <div key={lang} className="flex items-start gap-2">
                                <span className="font-mono text-xs text-gray-400 w-8 pt-1">
                                  {lang}
                                </span>
                                <div className="flex-1 space-y-1">
                                  {aliases.map((a) => (
                                    <AliasRow key={a.id} item={a} onChanged={refresh} />
                                  ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                </div>
              </section>
            ))}
        </div>
      )}
    </div>
  );
}

// ----- Alias-row (inline edit) ---------------------------------------------

function AliasRow({ item, onChanged }: { item: LearnedAlias; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.label);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!draft.trim() || draft === item.label) {
      setEditing(false);
      setDraft(item.label);
      return;
    }
    setBusy(true);
    try {
      await updateLearnedAliasLabel(item.id, draft.trim());
      await onChanged();
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const toggleDisabled = async () => {
    setBusy(true);
    try {
      const reden = item.disabled ? undefined : prompt("Reden (optioneel):") || undefined;
      await setLearnedAliasDisabled(item.id, !item.disabled, reden);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const hardDelete = async () => {
    if (!confirm(`Alias '${item.label}' HARD verwijderen (geen rollback)?`)) return;
    setBusy(true);
    try {
      await deleteLearnedAlias(item.id);
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`group flex items-center gap-2 rounded px-2 py-1 text-sm ${
        item.disabled ? "bg-gray-50 text-gray-400 italic" : "hover:bg-gray-50"
      }`}
    >
      {editing ? (
        <>
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") {
                setEditing(false);
                setDraft(item.label);
              }
            }}
            disabled={busy}
            className="flex-1 rounded border px-2 py-0.5 text-sm"
          />
          <button
            onClick={save}
            disabled={busy}
            className="text-xs text-blue-600 hover:text-blue-800 px-1"
          >
            Opslaan
          </button>
          <button
            onClick={() => {
              setEditing(false);
              setDraft(item.label);
            }}
            disabled={busy}
            className="text-xs text-gray-500 hover:text-gray-700 px-1"
          >
            Annuleren
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 truncate" title={item.label}>
            {item.label}
          </span>
          <span className="text-xs text-gray-400 opacity-0 group-hover:opacity-100">
            {new Date(item.added_at).toLocaleDateString()}
          </span>
          <button
            onClick={() => setEditing(true)}
            disabled={busy}
            className="opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-blue-700 px-1"
          >
            Bewerk
          </button>
          <button
            onClick={toggleDisabled}
            disabled={busy}
            className="opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-orange-700 px-1"
            title={item.disabled ? "Weer activeren" : "Soft-disable (admin-pad)"}
          >
            {item.disabled ? "Re-enable" : "Disable"}
          </button>
          <button
            onClick={hardDelete}
            disabled={busy}
            className="opacity-0 group-hover:opacity-100 text-xs text-gray-500 hover:text-red-700 px-1"
            title="Hard verwijderen (geen rollback)"
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}

// ----- Nieuwe alias toevoegen (admin-form) ---------------------------------

const KNOWN_DOCTYPES = [
  "invoice",
  "despatch_advice",
  "packing_list",
  "cmr",
  "bill_of_lading",
  "air_waybill",
  "forwarding_instructions",
];
const KNOWN_LANGS = ["nl", "en", "de", "fr"];

function AddAliasForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [doctype, setDoctype] = useState("invoice");
  const [veld, setVeld] = useState("");
  const [taal, setTaal] = useState("nl");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!veld.trim() || !label.trim()) {
      setError("Veld en label zijn verplicht.");
      return;
    }
    setBusy(true);
    try {
      await createLearnedAlias({
        doctype,
        veld_canoniek: veld.trim(),
        taal,
        label: label.trim(),
      });
      setVeld("");
      setLabel("");
      setOpen(false);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded border border-blue-200 bg-blue-50 px-3 py-1 text-sm text-blue-700 hover:bg-blue-100"
      >
        + Nieuw…
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 rounded border bg-blue-50 px-2 py-1">
      <select
        value={doctype}
        onChange={(e) => setDoctype(e.target.value)}
        className="rounded border px-1 py-0.5 text-xs"
      >
        {KNOWN_DOCTYPES.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
      <input
        placeholder="veld_canoniek"
        value={veld}
        onChange={(e) => setVeld(e.target.value)}
        className="rounded border px-1 py-0.5 text-xs w-32"
      />
      <select
        value={taal}
        onChange={(e) => setTaal(e.target.value)}
        className="rounded border px-1 py-0.5 text-xs"
      >
        {KNOWN_LANGS.map((l) => (
          <option key={l} value={l}>
            {l}
          </option>
        ))}
      </select>
      <input
        placeholder="label (bv. FactuurNr.)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        className="rounded border px-1 py-0.5 text-xs w-40"
      />
      <button
        onClick={submit}
        disabled={busy}
        className="rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700"
      >
        OK
      </button>
      <button
        onClick={() => {
          setOpen(false);
          setError(null);
        }}
        className="rounded border px-2 py-0.5 text-xs"
      >
        ×
      </button>
      {error && <div className="w-full text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}
